/**
 * TensionsCarousel — §02 single-tension deck.
 *
 * Empty state (index === null):
 *   eyebrow hint + pill button to begin + decorative dots.
 *
 * Active state (index ≥ 0):
 *   counter (Tensión · NN / TT), faint adjacent labels at the edges as a
 *   "deck of cards" hint, the WORD-A vs WORD-B headline (re-shuffles per
 *   index because of key={index}), description paragraph, anchor pills,
 *   prev/next labels, and clickable dot tabs.
 *
 * Keyboard ←/→ advances once the carousel has been started. Per-tension
 * stage and word-shuffle keyframes live in `deepdive.css`, gated by
 * `prefers-reduced-motion: reduce`.
 *
 * Reset behavior: the parent `<DeepDive>` is re-keyed on album swap
 * (see App.tsx), so this component remounts with index === null whenever
 * the user picks a different album.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTrack } from '@/analytics';
import type { AdvanceMethod } from '@/analytics';
import type { Tension } from '@/types/album';

interface TensionsCarouselProps {
  tensions: readonly Tension[];
}

function splitVs(label: string): [string, string] {
  const parts = label.split(' vs ');
  if (parts.length === 2) return [parts[0]!, parts[1]!];
  return [label, ''];
}

export function TensionsCarousel({ tensions }: TensionsCarouselProps): JSX.Element {
  const total = tensions.length;
  const [index, setIndex] = useState<number | null>(null);
  const track = useTrack();

  /** Single funnel for navigation events. `null` from = first reveal
   *  (Begin); otherwise it's an Advance with the originating method. */
  const advance = useCallback(
    (to: number, method: AdvanceMethod) => {
      setIndex((prev) => {
        if (prev === null) return prev;
        if (to === prev) return prev;
        track('Tensions Advance', { from: prev, to, method });
        return to;
      });
    },
    [track]
  );

  const begin = useCallback(() => {
    track('Tensions Begin', { count: total });
    setIndex(0);
  }, [track, total]);

  // Keyboard arrow nav, only attached after the carousel has been started.
  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' && index < total - 1) {
        e.preventDefault();
        advance(index + 1, 'keyboard');
      } else if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault();
        advance(index - 1, 'keyboard');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, total, advance]);

  if (index === null) {
    return (
      <div className="dd-tn-frame is-empty" aria-label="Tensiones centrales — comenzar">
        <div className="dd-tn-empty-eyebrow">— Tres tensiones · una a la vez</div>
        <button
          type="button"
          className="dd-tn-begin"
          onClick={begin}
          aria-label="Empezar a leer las tensiones"
        >
          <span className="prompt">Tap para leer la primera tensión</span>
          <span className="arrow" aria-hidden="true">
            →
          </span>
        </button>
        <div className="dd-tn-dots" aria-hidden="true">
          {tensions.map((_, i) => (
            <span key={i} className="dot" />
          ))}
        </div>
      </div>
    );
  }

  const tension = tensions[index]!;
  const [a, b] = splitVs(tension.label);
  const prev = index > 0 ? tensions[index - 1] : null;
  const next = index < total - 1 ? tensions[index + 1] : null;

  return (
    <div className="dd-tn-frame">
      <div className="dd-tn-meta">
        <span className="counter">
          {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
        </span>
      </div>

      {prev && (
        <div className="dd-tn-edge dd-tn-edge-l" aria-hidden="true">
          {splitVs(prev.label)[0]}
        </div>
      )}
      {next && (
        <div className="dd-tn-edge dd-tn-edge-r" aria-hidden="true">
          {splitVs(next.label)[1]}
        </div>
      )}

      <div key={index} className="dd-tn-stage" aria-live="polite">
        <h4 className="dd-tn-vs">
          <span className="word word-a">{a}</span>
          <span className="vs" aria-hidden="true">
            vs
          </span>
          <span className="word word-b">{b}</span>
        </h4>

        <p className="dd-tn-desc">{tension.description}</p>

        <ul className="dd-tn-anchors">
          {tension.anchors.map((anchor, j) => (
            <li key={j} className="dd-tn-anchor">
              <span className="track">{anchor.track}</span>, {anchor.note}
            </li>
          ))}
        </ul>
      </div>

      <div className="dd-tn-controls">
        <button
          type="button"
          className="dd-tn-btn"
          disabled={index === 0}
          onClick={() => advance(Math.max(0, index - 1), 'prev')}
          aria-label="Tensión anterior"
        >
          <span className="arrow" aria-hidden="true">
            ←
          </span>
          <span className="label">Anterior</span>
        </button>

        <div className="dd-tn-dots" role="tablist" aria-label="Cambiar de tensión">
          {tensions.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`dot ${i === index ? 'is-active' : ''}`}
              role="tab"
              aria-label={`Ir a tensión ${i + 1}`}
              aria-selected={i === index}
              onClick={() => advance(i, 'dot')}
            />
          ))}
        </div>

        <button
          type="button"
          className="dd-tn-btn"
          disabled={index === total - 1}
          onClick={() => advance(Math.min(total - 1, index + 1), 'next')}
          aria-label="Tensión siguiente"
        >
          <span className="label">Siguiente</span>
          <span className="arrow" aria-hidden="true">
            →
          </span>
        </button>
      </div>
    </div>
  );
}
